import { useMemo } from 'react';
import { type ChainflipAsset, getInternalAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import BigNumber from 'bignumber.js';
import { type LoanStatus } from '@/shared/graphql/generated/cache/graphql';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import {
  type LpLoanAccount,
  type Loan,
  type LoanAccountData,
} from '@/shared/hooks/useLpLoanAccount';
import { useOraclePrices } from '@/shared/hooks/useOraclePrice';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import { CF_NULL_ADDRESS } from '@/shared/utils/consts';
import { calculateLowLtvPenaltyPercent } from '@/shared/utils/lending';
import { pipsToPercent } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useStateChainAccount } from './useStateChainAccount';

export default function useLoanAccount(): Omit<LoanAccountData, 'loanAccounts'> & {
  penaltyFeePercent: number;
} {
  const { selectedAccount } = usePolkadot();
  const {
    targetLtvThresholdBps: borrowPowerThresholdBps,
    lowLtvPenaltyThresholdBps,
    lowLtvPenaltyBpsAtMaxLtv,
  } = useLendingConfig();
  const prices = useOraclePrices();
  // We need to use the collateral balances from the state chain account as
  // the cf_loan_accounts doesn't return these balances for accounts that have no active loans
  const { collateralBalances: stateChainCollateralBalances } = useStateChainAccount();

  const rpcResult = useRpcRequest({
    method: 'cf_loan_accounts',
    args: [selectedAccount?.idSs58],
    refetchInterval: 6_000,
    enabled: selectedAccount?.idSs58 !== undefined,
  });

  const rpcData = rpcResult.data?.at(0);
  const rpcCollateralBalances = Object.fromEntries(
    (rpcData?.collateral ?? []).map((item) => {
      const asset = getInternalAsset(item);
      return [asset, TokenAmount.fromAsset(item.amount, asset)];
    }),
  );

  const loans: Loan[] = useMemo(() => {
    if (!rpcData?.loans || !prices || !selectedAccount?.idSs58) return [];

    return rpcData.loans.map((loan) => {
      const chainflipAsset = getInternalAsset(loan.asset);

      const status: LoanStatus = rpcData.liquidation_status?.liquidation_type
        ? `${rpcData.liquidation_status.liquidation_type}Liquidation`
        : 'Active';

      const principalAmount = TokenAmount.fromAsset(loan.principal_amount, chainflipAsset);
      const usdPrice = prices[chainflipAsset]?.usdPrice ?? 0;

      return {
        id: loan.loan_id.toString(),
        asset: chainflipAsset,
        principalAmount,
        principalAmountUsd: principalAmount.toUsdNumber(usdPrice),
        status,
        lp: {
          lpIdSs58: selectedAccount.idSs58,
        },
      };
    });
  }, [rpcData, prices, selectedAccount?.idSs58]);

  const collateralBalances = useMemo(() => {
    if ((!stateChainCollateralBalances && !rpcCollateralBalances) || !prices) return [];

    return Object.entries(stateChainCollateralBalances ?? rpcCollateralBalances ?? {}).map(
      ([asset, amount]) => {
        const usdPrice = prices[asset as ChainflipAsset]?.usdPrice ?? 0;
        return {
          asset: asset as ChainflipAsset,
          amount,
          amountUsd: amount.toUsdNumber(usdPrice),
        };
      },
    );
  }, [stateChainCollateralBalances, rpcCollateralBalances, prices]);

  return useMemo(() => {
    if (!rpcResult.data || !selectedAccount?.idSs58 || !prices) {
      return {
        loanAccount: {
          isLiquidating: false,
          loans: [],
          collateralBalances,
          lpIdSs58: selectedAccount?.idSs58 ?? CF_NULL_ADDRESS,
          loanToValueBps: 0,
          borrowPowerUsedBps: 0,
          totalBorrowAmountUsd: 0,
          totalCollateralBalanceUsd: 0,
        },
        penaltyFeePercent: 0,
        isLoading: selectedAccount?.idSs58 !== undefined,
      };
    }

    const totalBorrowAmountUsd = loans.reduce((acc, loan) => acc + loan.principalAmountUsd, 0);

    const totalCollateralBalanceUsd = collateralBalances.reduce(
      (acc, collateral) => acc + collateral.amountUsd,
      0,
    );

    const loanToValueBps = totalCollateralBalanceUsd
      ? Math.trunc((totalBorrowAmountUsd / totalCollateralBalanceUsd) * 10_000)
      : new BigNumber(rpcData?.ltv_ratio ?? 0).div(100_000).toNumber();

    const borrowPowerUsedBps =
      totalCollateralBalanceUsd && borrowPowerThresholdBps
        ? Math.trunc(
            (totalBorrowAmountUsd /
              (totalCollateralBalanceUsd * (borrowPowerThresholdBps / 10_000))) *
              10_000,
          )
        : 0;

    const loanAccount: LpLoanAccount = {
      loans,
      collateralBalances,
      loanToValueBps,
      borrowPowerUsedBps,
      totalBorrowAmountUsd,
      totalCollateralBalanceUsd,
      lpIdSs58: selectedAccount.idSs58,
      isLiquidating: isNotNullish(rpcData?.liquidation_status),
    };

    const penaltyFeePercent = calculateLowLtvPenaltyPercent(
      pipsToPercent(loanToValueBps),
      pipsToPercent(lowLtvPenaltyThresholdBps),
      pipsToPercent(lowLtvPenaltyBpsAtMaxLtv),
    );

    return {
      loanAccount,
      penaltyFeePercent,
      isLoading: rpcResult.isLoading,
    };
  }, [
    rpcResult.data,
    selectedAccount?.idSs58,
    loans,
    collateralBalances,
    borrowPowerThresholdBps,
    lowLtvPenaltyThresholdBps,
    lowLtvPenaltyBpsAtMaxLtv,
    prices,
  ]);
}
